import {
  computed,
  defineComponent,
  getCurrentInstance,
  nextTick,
  provide,
  ref,
  renderSlot,
  watch,
} from 'vue'
import { Plus } from '@jd/jdesign-icons-vue'
import {
  buildProps,
  definePropType,
  isNumber,
  isString,
  isUndefined,
} from '@jdesign-vue/utils'
import { JdAffix } from '@jdesign-vue/components/affix'
import { EVENT_CODE, UPDATE_MODEL_EVENT } from '@jdesign-vue/constants'
import JdIcon from '@jdesign-vue/components/icon'
import {
  useDeprecated,
  useNamespace,
  useOrderedChildren,
} from '@jdesign-vue/hooks'
import { tabsRootContextKey } from './constants'
import TabNav from './tab-nav'
import TabNavFollow from './tab-nav-follow'
import { normalizeStickyConfig } from './sticky'

import type { TabNavInstance } from './tab-nav'
import type { TabsPaneContext } from './constants'
import type { ExtractPropTypes } from 'vue'
import type { Awaitable } from '@jdesign-vue/utils'

export type TabPaneName = string | number

export const tabsProps = buildProps({
  type: {
    type: String,
    values: ['card', 'border-card', 'block', ''],
    default: '',
  },
  activeName: {
    type: [String, Number],
  },
  closable: Boolean,
  addable: Boolean,
  followAdd: Boolean,
  modelValue: {
    type: [String, Number],
  },
  editable: Boolean,
  tabPosition: {
    type: String,
    values: ['top', 'right', 'bottom', 'left'],
    default: 'top',
  },
  beforeLeave: {
    type: definePropType<
      (newName: TabPaneName, oldName: TabPaneName) => Awaitable<void | boolean>
    >(Function),
    default: () => true,
  },
  stretch: Boolean,
  splitLine: {
    type: Boolean,
    default: true,
  },
  scrollHeight: {
    type: String,
    default: '100%',
  },
  size: {
    type: String,
    default: '',
  },
  surface: {
    type: Boolean,
  },
  sticky: {
    type: [Boolean, Object],
    default: false,
  },
} as const)
export type TabsProps = ExtractPropTypes<typeof tabsProps>

const isPaneName = (value: unknown): value is string | number =>
  isString(value) || isNumber(value)

export const tabsEmits = {
  [UPDATE_MODEL_EVENT]: (name: TabPaneName) => isPaneName(name),
  tabClick: (pane: TabsPaneContext, ev: Event) => ev instanceof Event,
  tabChange: (name: TabPaneName) => isPaneName(name),
  edit: (paneName: TabPaneName | undefined, action: 'remove' | 'add') =>
    ['remove', 'add'].includes(action),
  tabRemove: (name: TabPaneName) => isPaneName(name),
  tabAdd: () => true,
}
export type TabsEmits = typeof tabsEmits

export type TabsPanes = Record<number, TabsPaneContext>

export default defineComponent({
  name: 'JdTabs',

  props: tabsProps,
  emits: tabsEmits,

  setup(props, { emit, slots, expose }) {
    const ns = useNamespace('tabs')

    // 处理吸顶配置
    const stickyConfig = computed(() => normalizeStickyConfig(props.sticky))

    // 吸顶时的样式处理
    const stickyHeaderStyle = computed(() => {
      if (!stickyConfig.value) return {}
      return {
        backgroundColor: stickyConfig.value.backgroundColor || undefined,
      }
    })

    const {
      children: panes,
      addChild: registerPane,
      removeChild: unregisterPane,
    } = useOrderedChildren<TabsPaneContext>(getCurrentInstance()!, 'JdTabPane')

    const nav$ = ref<TabNavInstance>()
    const currentName = ref<TabPaneName>(
      props.modelValue ?? props.activeName ?? '0'
    )

    const changeCurrentName = (value: TabPaneName) => {
      currentName.value = value
      emit(UPDATE_MODEL_EVENT, value)
      emit('tabChange', value)
    }

    const setCurrentName = async (value?: TabPaneName) => {
      // should do nothing.
      if (currentName.value === value || isUndefined(value)) return

      try {
        const canLeave = await props.beforeLeave?.(value, currentName.value)
        if (canLeave !== false) {
          changeCurrentName(value)

          // call exposed function, Vue doesn't support expose in typescript yet.
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          nav$.value?.removeFocus?.()
        }
      } catch {}
    }

    const handleTabClick = (
      tab: TabsPaneContext,
      tabName: TabPaneName,
      event: Event
    ) => {
      if (tab.props.disabled) return
      setCurrentName(tabName)
      emit('tabClick', tab, event)
    }

    const handleTabRemove = (pane: TabsPaneContext, ev: Event) => {
      if (pane.props.disabled || isUndefined(pane.props.name)) return
      ev.stopPropagation()
      emit('edit', pane.props.name, 'remove')
      emit('tabRemove', pane.props.name)
    }

    const handleTabAdd = () => {
      emit('edit', undefined, 'add')
      emit('tabAdd')
    }

    useDeprecated(
      {
        from: '"activeName"',
        replacement: '"model-value" or "v-model"',
        scope: 'JdTabs',
        version: '2.3.0',
        ref: 'https://jdesign-vue.org/en-US/component/tabs.html#attributes',
        type: 'Attribute',
      },
      computed(() => !!props.activeName)
    )

    watch(
      () => props.activeName,
      (modelValue) => setCurrentName(modelValue)
    )

    watch(
      () => props.modelValue,
      (modelValue) => setCurrentName(modelValue)
    )

    watch(currentName, async () => {
      await nextTick()
      // call exposed function, Vue doesn't support expose in typescript yet.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      nav$.value?.scrollToActiveTab()
    })

    provide(tabsRootContextKey, {
      props,
      currentName,
      registerPane,
      unregisterPane,
    })

    expose({
      currentName,
    })

    return () => {
      const newButton =
        (props.editable || props.addable) && !props.followAdd ? (
          <span
            class={ns.e('new-tab')}
            tabindex="0"
            onClick={handleTabAdd}
            onKeydown={(ev: KeyboardEvent) => {
              if (ev.code === EVENT_CODE.enter) handleTabAdd()
            }}
          >
            <JdIcon class={ns.is('icon-plus')}>
              <Plus />
            </JdIcon>
          </span>
        ) : null

      const extra = slots.extra ? (
        <div class={ns.e('extra')}>{renderSlot(slots, 'extra')}</div>
      ) : null

      const NavComponent = props.followAdd ? TabNavFollow : TabNav
      const headerContent = (
        <div
          class={[
            ns.e('header'),
            ns.is(props.tabPosition),
            ns.is('splitLine', props.splitLine),
            ns.is(props.followAdd ? 'follow-add-header' : ''),
          ]}
          style={stickyHeaderStyle.value}
        >
          {extra}
          {newButton}
          <NavComponent
            ref={nav$}
            currentName={currentName.value}
            editable={props.editable}
            addable={props.addable}
            followAdd={props.followAdd}
            type={props.type}
            panes={panes.value}
            stretch={props.stretch}
            onTabClick={handleTabClick}
            onTabRemove={handleTabRemove}
            onTabAdd={handleTabAdd}
          />
        </div>
      )

      // 吸顶功能处理 - 仅header吸顶
      const header = stickyConfig.value ? (
        <JdAffix
          offset={stickyConfig.value.offset ?? 0}
          target={stickyConfig.value.target || ''}
          zIndex={stickyConfig.value.zIndex || 100}
          position={stickyConfig.value.position || 'top'}
        >
          {headerContent}
        </JdAffix>
      ) : (
        headerContent
      )


      const panels = (
        <div class={ns.e('content')}>{renderSlot(slots, 'default')}</div>
      )

      return (
        <div
          class={[
            ns.b(),
            ns.m(props.size),
            ns.m(props.tabPosition),
            {
              [ns.m('card')]: props.type === 'card',
              [ns.m('border-card')]: props.type === 'border-card',
              [ns.m('block')]: props.type === 'block',
              [ns.m('surface')]:
                (props.type === 'card' || props.type === 'border-card') &&
                props.surface,
              [ns.m('sticky')]: !!stickyConfig.value,
            },
          ]}
        >
          {...props.tabPosition !== 'bottom'
            ? [header, panels]
            : [panels, header]}
        </div>
      )
    }
  },
})
